import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import styles from "./index.module.scss";
import { useServices } from "@/services-context";
import { Url } from "@/helpers/url";
import newtab from "@/assets/newtab.svg";
import { LayoutStyleTypes, LayoutTypes, useLayoutType } from "@/util-react/layout.tsx";
import FixQuantity from "@/elements-react/fix-quantity";
import AddToCart from "@/elements-react/add-to-cart";
import PricesSelector from "@/elements-react/prices-selector";
import { Gtag } from "@/services/gtag.ts";
import { getCategoriesEcommerceItems, getItemName } from "@/helpers/google-events.ts";

export interface ExchangesAddToCartProps {
  product: any;
  dispatchSelectedQuantity?: number;
}

export const ExchangesAddToCart: React.FC<ExchangesAddToCartProps> = ({
  product,
  dispatchSelectedQuantity,
}) => {
  const { t } = useTranslation();
  const services = useServices();
  const layoutType = useLayoutType();
  const cart = services.cart;
  const layout = layoutType.layoutType ?? LayoutTypes.TYPE_DESKTOP;

  const exchangeProduct = product?.exchangesFinal?.[0];
  const productUrl = useMemo(
    () => exchangeProduct ? Url.to("product-page", { id: exchangeProduct.id }) : "#",
    [exchangeProduct]
  );

  // Активная цена
  const [activePrice, setActivePrice] = React.useState(
    exchangeProduct?.prices?.[0] ?? null
  );
  // Количество
  const [quantity, setQuantity] = React.useState(
    exchangeProduct?.minimalOrderQuantity ?? 1
  );

  // Максимальное количество
  const getMaxQuantity = activePrice?.availableStockCount ?? null;

  // В корзине
  const inCart = cart?.getItem(exchangeProduct?.id, activePrice?.id) ?? {};

  // Валюта
  const currentCurrency =
    services.currency.currencies?.find(
      (item: any) => item.code === activePrice?.price?.currency
    )?.code ?? "";

  // Итоговая цена
  const getTotalPriceOrder = () => {
    const total = (activePrice?.price?.amount ?? 0) * quantity;
    return Math.ceil(total * 100) / 100;
  };

  // Добавлено в корзину
  const isAddedToCart = !!inCart.added;

  // Обработка изменения цены
  const handleUpdatePrice = (e: any) => {
    setActivePrice(e.detail);
  };

  // Обработка изменения количества
  const handleSetQuantity = (e: any) => {
    let newQuantity = e.detail?.quantity ?? quantity;
    if (getMaxQuantity && newQuantity > getMaxQuantity) {
      newQuantity = getMaxQuantity;
      services.alerts?.addMessages?.(
        [t("2k1l3hf6f0682ddk.max-quantity-exceeded", "Quantity exceeds available stock")],
        "warning"
      );
    }
    setQuantity(newQuantity);
    if (inCart?.added) {
      cart.updateItem({
        cartProductId: inCart?.cartProductId,
        productId: exchangeProduct?.id,
        priceId: activePrice?.id,
        quantity: newQuantity,
      });
    }
  };

  // Добавить в корзину
  const handleAddToCartClick = () => {
    if (isAddedToCart) {
      Url.route(Url.to("cart-page"));
    } else {
      Gtag.pushEventEcommerce("add_to_cart", {
        items: [
          {
            item_name: getItemName(exchangeProduct),
            item_id: exchangeProduct?.id,
            price: parseFloat(activePrice.price.amount),
            item_brand: product.brand.name,
            index: 0,
            quantity: quantity,
            ...getCategoriesEcommerceItems(exchangeProduct?.categories, services),
          },
        ],
      });

      cart.updateItem({
        cartProductId: inCart?.cartProductId,
        productId: exchangeProduct?.id,
        priceId: activePrice?.id,
        quantity: quantity + (inCart.added ? inCart.quantity : 0),
      });
    }
  };

  return (
    <div className={styles.root}>
      <div
        className={classNames(
          styles.arrowUp,
          layout === LayoutTypes.TYPE_MOBILE && styles.mobileArrowUp
        )}
      />
      <div className={styles.content}>
        <div className={styles.title}>
          {t(
            "ansudpf894oiwahn.product-is-no-longer-produced",
            "This product is no longer produced – only {{quantity}} units left in stock",
            { quantity: dispatchSelectedQuantity }
          )}
        </div>
        <div>
          {t(
            "ne9riuhgnsierugn.but-we-have-an-official",
            "But we have an official replacement from the manufacturer. You can order the remaining units of product"
          )}
          <a className={styles.link} href={productUrl} target="_blank" rel="noopener noreferrer">
            {exchangeProduct?.mpn} <img src={newtab} alt="newtab" />
          </a>
        </div>
        <PricesSelector
          variations={exchangeProduct?.prices}
          onUpdatePrice={handleUpdatePrice}
        />
        <div className={styles.addToCartRow}>
          <FixQuantity
            className={classNames(styles.quantity, styles.wide)}
            style={{ maxWidth: 110 }}
            quantity={quantity}
            minQuantity={exchangeProduct?.minimalOrderQuantity ?? 1}
            maxQuantity={getMaxQuantity}
            factorQuantity={exchangeProduct?.minimalOrderQuantity ?? 1}
            onUpdateQuantity={handleSetQuantity}
          />
          <AddToCart
            type={LayoutStyleTypes.TYPE_LG}
            onClick={handleAddToCartClick}
            done={isAddedToCart}
          />
        </div>
        <div className={styles.bottomText}>
          {t(
            "bs79dpgihusebudf.total-of-the-additional",
            "Total of the additional order"
          )}
          : {currentCurrency} {getTotalPriceOrder()}
        </div>
      </div>
    </div>
  );
};

